import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter, DialogDescription } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Lock, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const CloseCampaignDialog = ({ open, onOpenChange, campaign }) => {
  const [stage, setStage] = useState('confirm');

  const close = () => {
    setStage('calculating');
    setTimeout(() => setStage('done'), 2200);
    setTimeout(() => {
      onOpenChange(false);
      setStage('confirm');
    }, 4500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md' onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <DialogTitle>Close campaign?</DialogTitle>
          <DialogDescription>This triggers automatic calculation and writes results into your reports.</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {stage === 'confirm' && (
            <>
              <Alert className='mb-4'>
                <AlertTriangle size={14} strokeWidth={1.5} />
                <AlertTitle>Before you close</AlertTitle>
                <AlertDescription>
                  Late responses will not be counted unless you re-open the campaign. Once closed, results flow into
                  dashboards and SECR reports automatically.
                </AlertDescription>
              </Alert>
              <ul className='text-sm space-y-2'>
                <li className='flex items-start gap-2'>
                  <span className='w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0' />
                  <span>
                    Apply DEFRA factors for {campaign.reportingPeriod} to {campaign.completedCount} respondents
                  </span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0' />
                  <span>Extrapolate each location's totals to its FTE</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0' />
                  <span>Write rows into Home Working &amp; employee commuting entities</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0' />
                  <span>Send closure notification to you via email</span>
                </li>
              </ul>
            </>
          )}

          {stage === 'calculating' && (
            <div className='flex flex-col items-center py-6 text-center'>
              <div className='w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-accent mb-4'>
                <Loader2 size={20} strokeWidth={1.5} className='animate-spin' />
              </div>
              <div className='font-medium mb-1'>Calculating results</div>
              <div className='text-xs text-muted-foreground'>
                Applying factors and extrapolating... usually &lt; 2 minutes.
              </div>
            </div>
          )}

          {stage === 'done' && (
            <div className='flex flex-col items-center py-6 text-center'>
              <div className='w-12 h-12 rounded-full bg-success-bg flex items-center justify-center text-success mb-4'>
                <CheckCircle2 size={20} strokeWidth={1.5} />
              </div>
              <div className='font-medium mb-1'>Campaign closed</div>
              <div className='text-xs text-muted-foreground'>
                Results are available under the Results tab and in your reports pipeline.
              </div>
            </div>
          )}
        </DialogBody>

        {stage === 'confirm' && (
          <DialogFooter>
            <Button variant='ghost' onClick={() => onOpenChange(false)}>
              Keep open
            </Button>
            <Button onClick={close}>
              <Lock size={14} strokeWidth={1.5} />
              Close &amp; calculate
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CloseCampaignDialog;
